import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from "@elaniintc/goes-design-system";
import { NavLink } from "react-router";
import { STATUS_CONFIG, type ProcessStatus } from "../constants";

interface EllipsisDropdownProps {
  entity: string;
  id: string;
  onDuplicate: () => void;
  onApproved: (id: string, currentStatus: ProcessStatus) => void;
  onSelect: () => void;
  status: ProcessStatus;
  canApprove?: boolean;
}
export default function EllipsisDropdown({
  entity,
  id,
  onDuplicate,
  onApproved,
  onSelect,
  status,
  canApprove,
}: EllipsisDropdownProps) {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.processes.update);
  const canCreate = hasPermission(Permissions.processes.create);

  const getActionLabel = (status: string): string | null => {
    if (status === "ON_CONSTRUCTION" && canApprove) {
      return "Activar proceso";
    }
    if (canCreate) {
      if (status === "ON_CONSTRUCTION") {
        return null;
      }
      return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label ?? "";
    }
    return null;
  };

  const actionLabel = getActionLabel(status);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8 cursor-pointer">
          <Icon type="far" name="ellipsis-vertical" className="text-current" />
          <span className="sr-only">
            Acciones para {entity} {id}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[222px] min-w-56 rounded-lg border-neutral text-body font-normal p-2 font-geist"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            asChild
          >
            <NavLink to={`/procesos/${id}`} className="w-full">
              Ver proceso
            </NavLink>
          </DropdownMenuItem>
          {canEdit && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onSelect();
              }}
            >
              Editar proceso
            </DropdownMenuItem>
          )}
          {canCreate && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDuplicate();
              }}
            >
              Duplicar proceso
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          >
            <NavLink to={`/procesos/${id}?type=historial`} className="w-full">
              Ver log del proceso
            </NavLink>
          </DropdownMenuItem>
          {actionLabel && (
            <>
              <DropdownMenuSeparator className="h-[1px] bg-[#B7C0CC] m-3" />
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onApproved(id, status);
                }}
              >
                {actionLabel}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
